import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-366e519c"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "ticket-availability-search__body-title" }
const _hoisted_2 = { class: "ticket-availability-search__body-title" }
const _hoisted_3 = {
  key: 0,
  class: "ticket-availability-search__body-title"
}
const _hoisted_4 = {
  key: 1,
  class: "ticket-availability-search__body-title"
}
const _hoisted_5 = {
  key: 0,
  class: "ticket-availability-search__price ticket-availability-search__price--base ticket-availability-search__price--discounted"
}

import { computed, PropType, ref, watch } from 'vue'
import { PublicService } from '@/models/public/bookingWidget.model'
import { MinimumTimeslot } from '@/models/public/service.model'
import BaseButton from '@/components/BaseButton.vue'
import isValid from 'date-fns/isValid'
import { getValidationErrorData } from '@/utils/errors'
import {
  createTicketItemOrderItems,
  useCreateOrderMutation,
  useFetchCartByUuid,
} from '@/queries/cart'
import {
  ICreateTicketServiceOrderItem,
  Order,
} from '@/models/public/order.model'
import DurationTag from '@/components/booking-widget/DurationTag.vue'
import { isNumeric } from '@/utils/general'
import { formatCurrency, formatServerDateTime } from '@/utils/formatters'
import {
  useFetchTicketItemDateAvailabilityListMutation,
  useFetchTicketItemPriceInformationMutation,
} from '@/queries/service/serviceTicket'
import {
  ITicketItemDateAvailability,
  ITicketItemDateAvailabilityTimeslot,
  ITicketServiceItemPriceInformationBodyItem,
  ITicketServicePriceInformation,
} from '@/models/service/serviceTicket.model'
import TicketSalesItems from '@/components/booking-widget/TicketSalesItems.vue'
import _ from 'lodash'
import isSameDay from 'date-fns/isSameDay'


export default /*#__PURE__*/_defineComponent({
  __name: 'TicketCard',
  props: {
  selectedService: {
    type: Object as PropType<PublicService>,
    required: true,
  },
  selectedStartDate: {
    type: Object as PropType<Date | null>,
    default: null,
  },
  selectedAdultCount: {
    type: Number,
    default: 1,
  },
  selectedSlots: {
    type: Array as PropType<MinimumTimeslot[]>,
    default: () => [],
  },
  order: {
    type: Object as PropType<Order | null>,
    default: null,
  },
  ticketAvailabilities: {
    type: Array as PropType<ITicketItemDateAvailability[]>,
    default: () => [],
  },
},
  emits: ["update:selectedStartDate", "update:selectedAdultCount", "update:order", "on-timeslot-select"],
  setup(__props, { emit }: { emit: ({
  (e: 'update:selectedStartDate', newDate: Date): void
  (e: 'update:selectedAdultCount', newAdultCount: number): void
  (e: 'update:order', newOrder: Order): void
  (
    e: 'on-timeslot-select',
    slotId: number | undefined,
    shouldUnselect: boolean,
  ): void
}), expose: any, slots: any, attrs: any }) {

const props = __props





const timeslotQuantities = ref<ITicketServiceItemPriceInformationBodyItem[]>([])
const priceInfo = ref<ITicketServicePriceInformation | null>(null)
const errors = ref<Record<string, string | undefined>>({})
const loading = ref<boolean>(false)
const noAvailability = ref<boolean>(false)

const { mutateAsync: createOrder } = useCreateOrderMutation()
const { mutateAsync: fetchPriceInfo } =
  useFetchTicketItemPriceInformationMutation()
const { mutateAsync: fetchCart } = useFetchCartByUuid()

const isDiscounted = computed<boolean>(
  () => priceInfo.value?.base_total !== priceInfo.value?.discounted_total,
)

const hasPrice = computed<boolean>(
  () =>
    !!priceInfo.value &&
    (priceInfo.value.base_total !== 0 ||
      priceInfo.value.discounted_total !== 0),
)

const formattedBasePrice = computed<string>(() =>
  isNumeric(priceInfo.value?.base_total)
    ? formatCurrency(priceInfo.value?.base_total)
    : '- €',
)

const formattedDiscountedPrice = computed<string>(() =>
  isNumeric(priceInfo.value?.discounted_total)
    ? formatCurrency(priceInfo.value?.discounted_total)
    : '- €',
)

const timeslotsPayload = computed<ITicketServiceItemPriceInformationBodyItem[]>(
  () =>
    timeslotQuantities.value.map((slot) => ({
      ...slot,
      start: formatServerDateTime(slot.start) as string,
      end: formatServerDateTime(slot.end) as string,
    })),
)

watch(
  () => props.selectedService,
  () => {
    errors.value = {}
    clear()
  },
)

watch(
  () => [props.selectedStartDate],
  (newValues, prevValues) => {
    if (prevValues[0] && isSameDay(newValues[0], prevValues[0])) {
      return
    }
    clear()
  },
)

watch(
  () => [timeslotQuantities.value],
  () => {
    updatePriceInfo()
  },
)

watch(
  () => [props.selectedSlots],
  () => {
    handleSelectedSlotsChange()
  },
)

const clear = () => {
  timeslotQuantities.value = []
}

const updatePriceInfo = async () => {
  try {
    const price = await fetchPriceInfo({
      service: props.selectedService.hash_id,
      isPreview: false,
      body: {
        items: timeslotsPayload.value,
        order_item: null,
        group_service: undefined,
      },
    })
    if (price) {
      priceInfo.value = price
    }
  } catch (error) {
    // @ts-ignore
    errors.value = getValidationErrorData(error)
  }
}

const addTicketItemToCart = async () => {
  loading.value = true
  if (errors.value.order) {
    delete errors.value.order
  }
  let orderUuid = props.order?.uuid
  const payload: ICreateTicketServiceOrderItem = {
    service: props.selectedService.id,
    items: timeslotsPayload.value,
    referral_type: '',
    referral_code: '',
    item_group: null,
  }
  try {
    if (!orderUuid) {
      const order = await createOrder()
      if (order) {
        orderUuid = order.uuid
      }
    }
    if (orderUuid) {
      await createTicketItemOrderItems(orderUuid, payload)
      const updatedOrderData = await fetchCart(orderUuid)
      emit('update:order', updatedOrderData)
    }
  } catch (error: unknown) {
    // @ts-ignore
    errors.value.order = getValidationErrorData(error)
  }
  loading.value = false
}

const handleBuy = async () => {
  if (props.order?.uuid) {
    window.open(window.location.origin + '/order/' + props.order.uuid)
  }
}

function updateDate(newDate: Date) {
  if (!isValid(newDate)) return

  emit('update:selectedStartDate', newDate)
}

const handleTimeslotIncrement = (
  timeslot: ITicketItemDateAvailabilityTimeslot,
  increment: number,
) => {
  let timeslotWasRemoved = false
  const newTimeslotQuantities = timeslotQuantities.value
    .map((slot: ITicketServiceItemPriceInformationBodyItem) => {
      if (timeslot.id === slot.id) {
        const newQuantity = slot.quantity + increment
        if (newQuantity === 0) {
          timeslotWasRemoved = true
          return null
        }
        return { ...slot, quantity: slot.quantity + increment }
      }
      return slot
    })
    .filter(
      (slot): slot is ITicketServiceItemPriceInformationBodyItem =>
        slot !== null,
    )

  const timeslotAdded = _.isEmpty(
    _.xorWith(newTimeslotQuantities, timeslotQuantities.value, _.isEqual),
  ) // Are arrays deeply equal
  if (timeslotAdded) {
    newTimeslotQuantities.push({
      id: timeslot.id,
      ticket_item: timeslot.ticket_item,
      quantity: 1,
      start: timeslot.start,
      end: timeslot.end,
    })
  }

  emit('on-timeslot-select', timeslot.id, timeslotWasRemoved)

  timeslotQuantities.value = newTimeslotQuantities
}

const handleSelectedSlotsChange = () => {
  handleAddedSelectedSlot()
  handleRemovedSelectedSlot()
}

const handleAddedSelectedSlot = () => {
  let addedSelectedSlot: MinimumTimeslot | undefined
  props.selectedSlots.forEach((selectedSlot: MinimumTimeslot) => {
    if (
      !timeslotQuantities.value.find(
        (timeslotQuantity: ITicketServiceItemPriceInformationBodyItem) =>
          selectedSlot.id === timeslotQuantity.id,
      )
    ) {
      addedSelectedSlot = selectedSlot
    }
  })
  if (addedSelectedSlot) {
    props.ticketAvailabilities.forEach(
      (availability: ITicketItemDateAvailability) => {
        const matchingTimeslot = availability.timeslots.find(
          (ticketTimeslot: ITicketItemDateAvailabilityTimeslot) =>
            ticketTimeslot.id === addedSelectedSlot?.id,
        )
        if (matchingTimeslot) {
          timeslotQuantities.value = [
            ...timeslotQuantities.value,
            {
              id: matchingTimeslot.id,
              ticket_item: matchingTimeslot.ticket_item,
              quantity: 1,
              start: matchingTimeslot.start,
              end: matchingTimeslot.end,
            },
          ]
        }
      },
    )
  }
}

const handleRemovedSelectedSlot = () => {
  let removedTicketTimeslot:
    | ITicketServiceItemPriceInformationBodyItem
    | undefined
  timeslotQuantities.value.forEach(
    (timeslotQuantity: ITicketServiceItemPriceInformationBodyItem) => {
      if (
        !props.selectedSlots.find(
          (selectedSlot: MinimumTimeslot) =>
            selectedSlot.id === timeslotQuantity.id,
        )
      ) {
        removedTicketTimeslot = timeslotQuantity
      }
    },
  )

  if (removedTicketTimeslot) {
    timeslotQuantities.value = timeslotQuantities.value.filter(
      (timeslotQuantity: ITicketServiceItemPriceInformationBodyItem) =>
        timeslotQuantity.id !== removedTicketTimeslot?.id,
    )
  }
}

return (_ctx: any,_cache: any) => {
  const _component_DatePicker = _resolveComponent("DatePicker")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("h4", _hoisted_1, _toDisplayString(_ctx.$t('whenAreYouGoing')), 1),
    _createVNode(_component_DatePicker, {
      name: "arrival",
      "model-value": __props.selectedStartDate,
      label: _ctx.$t('arrival'),
      errors: [
      errors.value?.arrival,
      errors.value?.consecutive,
      errors.value?.minDuration,
      errors.value?.maxDuration,
    ],
      "onUpdate:modelValue": updateDate
    }, null, 8, ["model-value", "label", "errors"]),
    _createElementVNode("h4", _hoisted_2, _toDisplayString(_ctx.$t('howManyAreYou')), 1),
    (noAvailability.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_ctx.$t('bookingWidget.noAvailability')), 1))
      : _createCommentVNode("", true),
    _createVNode(TicketSalesItems, {
      "selected-timeslots": timeslotQuantities.value,
      "selected-service": __props.selectedService,
      "selected-slots": __props.selectedSlots,
      errors: errors.value,
      "available-ticket-items": __props.ticketAvailabilities,
      onOnTimeslotIncrement: handleTimeslotIncrement
    }, null, 8, ["selected-timeslots", "selected-service", "selected-slots", "errors", "available-ticket-items"]),
    (_unref(hasPrice))
      ? (_openBlock(), _createElementBlock("h4", _hoisted_4, [
          _createTextVNode(_toDisplayString(_ctx.$t('price.priceLabel')) + " ", 1),
          (_unref(isDiscounted))
            ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(_unref(formattedBasePrice)), 1))
            : _createCommentVNode("", true),
          _createElementVNode("span", {
            class: _normalizeClass(["ticket-availability-search__price", [_unref(isDiscounted) && 'ticket-availability-search__price--discounted']])
          }, _toDisplayString(_unref(formattedDiscountedPrice)), 3)
        ]))
      : _createCommentVNode("", true),
    _createVNode(BaseButton, {
      loading: loading.value,
      disabled: !_unref(hasPrice) || Object.keys(errors.value).length,
      onOnClick: addTicketItemToCart
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(_ctx.$t('bookingWidget.addToCart')), 1)
      ]),
      _: 1
    }, 8, ["loading", "disabled"]),
    _createVNode(BaseButton, {
      class: "ticket-availability-search__button",
      loading: loading.value,
      disabled: !__props.order,
      onOnClick: handleBuy
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(_ctx.$t('bookingWidget.goToBuy')), 1)
      ]),
      _: 1
    }, 8, ["loading", "disabled"])
  ], 64))
}
}

})